import React, { useState, useEffect, useRef } from 'react';
import { withTranslation, i18n } from 'config/i18n';

// Material UI core
import { makeStyles, alpha } from '@material-ui/core/styles';
import { Typography, Grid } from '@material-ui/core';
// React compoentns
import Tabs from 'components/shared/Tabs';
import EarningsBalance from 'components/ownerPages/earningsAndPayouts/earningsBalance';
import PayoutMethodSettings from 'components/ownerPages/earningsAndPayouts/payoutMethodSettings';
import TransactionHistory from 'components/ownerPages/earningsAndPayouts/transactionHistory';
import PayeeVerification from 'components/ownerPages/earningsAndPayouts/payeeVerification';

import axios, { API } from 'config/axios';
import useHandleResponse from 'utils/useHandleResponse';
import { useLayoutSnackbar, useLayoutLoadingSpinner } from 'components/shared/Layout';

const { envVars } = require('next/config').default().publicRuntimeConfig;

const { HYPERWALLET_API_URL } = envVars;

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(1.5),
  },
  title: {
    fontSize: theme.typography.pxToRem(30),
    fontWeight: 600,
    letterSpacing: theme.typography.pxToRem(0.3),
    padding: theme.spacing(1, 0),
    textTransform: 'capitalize',
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(24),
    },
  },
  content: {
    margin: theme.spacing(1.5, 0, 4),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0),
    },
  },
  body: {},
  separator: {
    height: theme.spacing(6),
  },
  // eslint-disable-next-line mui-unused-classes/unused-classes
  '@global': {
    '#VerificationUI': {
      marginTop: theme.spacing(3),
    },
    '#VerificationUI #hw-parent': {
      backgroundColor: theme.palette.common.white,
      [theme.breakpoints.down('md')]: {
        display: 'block !important',
      },
    },

    '#VerificationUI .hw-parent': {
      // border: `1px solid ${theme.palette.grey['100']}`,
      borderRadius: theme.spacing(1.5),
      overflow: 'hidden',
    },

    '#VerificationUI .hw-breadcrumb-container': {
      background: theme.palette.primary.A000,
      padding: theme.spacing(1.5, 2),
      [theme.breakpoints.down('md')]: {
        borderBottom: `1px solid ${theme.palette.grey['100']}`,
      },
    },

    '#VerificationUI .hw-widget-container': {
      padding: theme.spacing(2),
    },

    'input, select, textarea': {
      width: '100%',
      border: '1px solid #E0E0E0',
      borderRadius: 10,
      padding: theme.spacing(1.25, 1.5),
      outline: 'none',
      fontSize: theme.typography.pxToRem(14),
      marginBottom: theme.spacing(2),
    },
    'input:focus, select:focus, textarea:focus': {
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.24)}`,
      borderColor: theme.palette.primary.light,
    },
    '.hw-field-groups h3': {
      marginTop: 0,
    },

    '.hw-fields label': {
      marginBottom: theme.spacing(0.5),
      display: 'block',
    },

    '#VerificationUI .hw-widget label': {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.grey['500'],
      marginBottom: theme.spacing(0.5),
      display: 'inline-block',
    },

    '#VerificationUI .hw-field-groups, #VerificationUI .hw-group-with-template': {
      marginBottom: theme.spacing(2),
    },
    '#VerificationUI fieldset': {
      border: `1px solid ${theme.palette.grey['100']}`,
      borderRadius: theme.spacing(1.25),
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },

    '#VerificationUI .hw-form-user button,     #VerificationUI .hw-widget button, #VerificationUI .btn-primary, .btn-link':
      {
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: 'none',
        borderRadius: 4,
        padding: theme.spacing(1.5, 2),
        cursor: 'pointer',
        fontSize: theme.typography.pxToRem(14),
        minWidth: theme.spacing(15),
        fontWeight: 'bold',
      },
    '#VerificationUI .hw-form-user button:hover, #VerificationUI .hw-widget button:hover, #VerificationUI .btn-primary:hover':
      {
        filter: 'brightness(0.95)',
      },
    '#VerificationUI .hw-error-message': {
      color: theme.palette.error.main,
      position: 'relative',
      top: theme.spacing(-1),
    },
    '#VerificationUI .hw-breadcrumb-links': {
      backgroundColor: 'transparent',
      marginBottom: theme.spacing(1),
      padding: 0,
      textAlign: 'left',
      textDecoration: 'underline',
      color: theme.palette.grey[600],
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 'normal',
    },
    '#VerificationUI .hw-breadcrumb-links.hw-breadcrumb-active-link': {
      color: theme.palette.common.black,
      fontWeight: 'bold',
      fontSize: theme.typography.pxToRem(14),
    },
    '#VerificationUI .hw-fields[class*="hw-field-account__"][class*="_front"],#VerificationUI .hw-fields[class*="hw-field-account__"][class*="_back"]':
      {
        border: '2px dashed #ccc',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        textAlign: 'center',
        cursor: 'pointer',
        marginBottom: theme.spacing(1.5),
        transition: 'border-color 0.2s ease',
      },
    '#VerificationUI .hw-fields[class*="hw-field-account__"][class*="_front"] .hw-error-message,#VerificationUI .hw-fields[class*="hw-field-account__"][class*="_back"] .hw-error-message':
      {
        top: 0,
      },

    '#VerificationUI .hw-fields[class*="hw-field-account"][class*="_front"]:hover, #VerificationUI .hw-fields[class*="hw-field-account"][class*="_back"]:hover':
      {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.A000,
      },
    '#VerificationUI .hw-fields[class*="hw-field-account__"][class*="_back"] .hw-button,#VerificationUI .hw-fields[class*="hw-field-account__"][class*="_front"] .hw-button':
      {
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(14),
        cursor: 'pointer',
      },
    '#VerificationUI .hw-fields[class*="hw-field-account__"][class*="_front"] span,#VerificationUI .hw-fields[class*="hw-field-account__"][class*="_back"] span': {
      maxWidth: '100%',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      color: theme.palette.grey[600],
    },
  },
}));

const PaymentsAndPayoutOwnerPages = ({ t }) => {
  const classes = useStyles();
  const { handleError } = useHandleResponse();
  const [snackbar, setSnackbar] = useLayoutSnackbar();
  const [, setLoading] = useLayoutLoadingSpinner();

  const tabs = [
    {
      href: '/owner/earnings_and_payouts',
      text: t('fe_earnings_and_payouts_page:title'),
      selected: true,
    },
  ];

  const [endpointData, setEndpointData] = useState(null); // Earnings and payouts
  const [transactionsData, setTransactionsData] = useState([]); // Transaction history page data
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const cancelToken1 = axios.CancelToken;
  const source1 = cancelToken1.source();

  // Earnings and payouts
  const getEarningsAndPayoutsData = async () => {
    try {
      const { data } = await axios.get(API.owner.earningsAndPayouts, {
        cancelToken: source1.token,
      });
      setEndpointData(data);
      const hyperwalletUserToken = data?.owner?.hyperwallet_user_token;
      if (hyperwalletUserToken) {
        // Load script only once
        const script = document.createElement('script');
        script.src = `${HYPERWALLET_API_URL}/rest/widgets/users/${hyperwalletUserToken}/${i18n.language}.v2_2_0.min.js`;
        script.async = true;

        script.onload = () => {
          setScriptLoaded(true);
        };

        document.body.appendChild(script);
      }
    } catch (error) {
      handleError(error, false);
    }
  };

  useEffect(() => {
    getEarningsAndPayoutsData();
    return () => {
      source1.cancel('Operation1 canceled by the user.');
    };
  }, []);

  // For Transaction History Component

  // To prevent sending multiple requests at the beginning
  const isFirstRun = useRef(false);

  // Pagination
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [page, setPage] = React.useState(
    (transactionsData?.pagination && +transactionsData.pagination.current_page - 1) || 0,
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const cancelToken2 = axios.CancelToken;
  const source2 = cancelToken2.source();

  const getFinancials = async () => {
    axios
      .get(API.owner.financials, {
        params: {
          page: page + 1, // +1 because endpoint start from 1 and pagination component is zero based
          per_page: rowsPerPage,
        },
        cancelToken: source2.token,
      })
      .then((res) => {
        if (res?.data) {
          setTransactionsData(res.data);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const getHyperwalletToken = async () => {
    try {
      const res = await axios.get(API.owner.hyperWalletUserToken);
      return res.data.token; // this will be returned to the caller
    } catch (error) {
      handleError(error);
      return null; // optionally return null if error occurs
    }
  };

  const getOwnerPayeeVerificationStatus = async () => {
    setLoading(true);

    try {
      const res = await axios.get(API.owner.payeeVerificationStatus);
      const payeeVerificationStatus = res.data.payee_verification_status;
      if (payeeVerificationStatus) {
        setEndpointData((prev) => ({
          ...prev,
          owner: {
            ...prev.owner,
            payee_verification_status: payeeVerificationStatus,
          },
        }));
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyBtnClick = async () => {
    setLoading(true);
    if (!window.HWWidgets) return;
    // Get JWT token first
    const token = await getHyperwalletToken();
    if (!token) {
      setSnackbar({
        ...snackbar,
        open: true,
        severity: 'error',
        message: t('fe_earnings_and_payouts_page:payee_verification.no_token_received_msg'),
      });
      return;
    }
    // Initialize widget with the token
    window.HWWidgets.initialize((onSuccess) => {
      onSuccess(token); // pass token to the widget
      setLoading(false);
      setScriptLoaded(false);
    });

    // Configure and display the user verification widget
    window.HWWidgets.users
      .configure({
        template: 'plain', // your template
        el: document.getElementById('VerificationUI'),
      })
      .display(() => {
        const container = document.getElementById('VerificationUI');
        container?.classList.add('hw-verify-themed');
      });

    // Listen for completion
    window.HWWidgets.events.on('widget:users:completed', async () => {
      const container = document.getElementById('VerificationUI');
      if (container) container.innerHTML = ''; // remove widget
      setSnackbar({
        ...snackbar,
        open: true,
        severity: 'success',
        message: t('fe_earnings_and_payouts_page:payee_verification.verification_in_progress_msg'),
      });
      getOwnerPayeeVerificationStatus();
    });
  };

  useEffect(() => {
    if (isFirstRun.current) {
      getFinancials();
    } else {
      isFirstRun.current = true;
    }
  }, [page]);

  useEffect(() => {
    if (isFirstRun.current) {
      if (page == 0) {
        getFinancials();
      } else setPage(0);
    }
  }, [rowsPerPage]);

  const handleRefreshPageData = () => {
    getEarningsAndPayoutsData();
    if (page == 0) {
      getFinancials();
    } else setPage(0);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h3" className={classes.title}>
        {t('fe_earnings_and_payouts_page:title')}
      </Typography>
      <div className={classes.content}>
        <Grid container spacing={3}>
          <Grid item lg={3} xs={12}>
            <Tabs items={tabs} />
          </Grid>
          <Grid item lg={9} xs={12}>
            <div className={classes.body}>
              <EarningsBalance
                endpointData={endpointData}
                refreshPageData={handleRefreshPageData}
              />
              <div className={classes.separator} />
              <PayoutMethodSettings
                transferMethods={
                  (endpointData?.owner && endpointData.owner.transfer_methods) || null
                }
                refreshPageData={handleRefreshPageData}
              />
              {endpointData?.owner && (
                <>
                  <div className={classes.separator} />
                  <PayeeVerification
                    payeeVerificationStatus={endpointData.owner.payee_verification_status}
                    payoutMethodsCount={endpointData.owner.payout_methods_count}
                    scriptLoaded={scriptLoaded}
                    handleVerifyBtnClick={handleVerifyBtnClick}
                  />
                </>
              )}
              <div className={classes.separator} />
              <TransactionHistory
                data={transactionsData}
                count={(transactionsData?.pagination && +transactionsData.pagination.total) || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default withTranslation(['fe_earnings_and_payouts_page'])(PaymentsAndPayoutOwnerPages);
